const BASE_URL = "/api/v1";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Fehlerantwort auswerten.
 *
 * Die Meldung darf nie leer bleiben (Befund B7): `statusText` ist bei HTTP/2
 * grundsätzlich leer, und eine Antwort ohne unsere Fehlerhülle (Proxy,
 * abgebrochene Verbindung, Fastify-Standardfehler) hatte damit gar keinen
 * Text — die Oberfläche zeigte ein rotes Banner mit dem Wort „Fehler" und
 * sonst nichts. Deshalb die Kette: eigene Hülle → Fastify-Standard → HTTP-Text
 * → Statuszeile.
 */
async function parseError(response: Response): Promise<ApiError> {
  const fallback = response.statusText || `HTTP ${response.status}`;
  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string; details?: unknown };
      message?: string;
    };
    const message = body.error?.message ?? body.message ?? "";
    return new ApiError(
      response.status,
      body.error?.code ?? "UNKNOWN",
      message.trim() || fallback,
      body.error?.details,
    );
  } catch {
    return new ApiError(response.status, "UNKNOWN", fallback);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  // Content-Type nur setzen, wenn wir wirklich einen Body senden — sonst
  // lehnt Fastifys JSON-Parser den Request mit 400 FST_ERR_CTP_EMPTY_JSON_BODY
  // ab und der Endpoint-Handler läuft gar nicht erst (z.B. /auth/logout).
  const init: RequestInit = {
    method,
    credentials: "include",
    headers: body !== undefined ? { "Content-Type": "application/json" } : {},
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  const response = await window.fetch(url, init);
  if (!response.ok) {
    throw await parseError(response);
  }
  // 204 No Content
  if (response.status === 204) {
    return undefined as unknown as T;
  }
  return response.json() as Promise<T>;
}

/**
 * Datei-Upload als `multipart/form-data` (Excel-Import der Wartungsplanung).
 *
 * Bewusst kein Sonderfall in `request`: Der Content-Type darf hier NICHT
 * gesetzt werden — der Browser ergänzt ihn samt `boundary` selbst, ein
 * händisch gesetzter Header ohne Boundary lässt Fastifys Multipart-Parser
 * die Datei nicht finden. Zusatzangaben (z. B. der Stichtag) laufen als
 * Query-Parameter mit, nicht als Formularfeld: Formularfelder sind erst
 * verlässlich gelesen, wenn der Datei-Stream durch ist, Query-Parameter
 * stehen dem Handler sofort zur Verfügung.
 */
async function upload<T>(
  path: string,
  file: File,
  params: Record<string, string> = {},
): Promise<T> {
  const form = new FormData();
  form.append("file", file, file.name);
  const query = Object.keys(params).length ? `?${new URLSearchParams(params).toString()}` : "";
  const response = await window.fetch(`${BASE_URL}${path}${query}`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!response.ok) {
    throw await parseError(response);
  }
  // 204 No Content — wie in `request`. Der Screenshot-Upload des Feedbacks
  // antwortet ohne Rumpf; `response.json()` würfe dort sonst einen
  // Parse-Fehler auf einer geglückten Anfrage.
  if (response.status === 204) {
    return undefined as unknown as T;
  }
  return response.json() as Promise<T>;
}

function get<T>(path: string): Promise<T> {
  return request<T>("GET", path);
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("POST", path, body);
}

function patch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("PATCH", path, body);
}

function put<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("PUT", path, body);
}

function del<T>(path: string): Promise<T> {
  return request<T>("DELETE", path);
}

export const api = { get, post, patch, put, delete: del, upload };
